/**
 * CSV Validation utilities for roster upload
 */

import { VALIDATION_MESSAGES } from '../../components/AddProvidersToRosterPage/constants';
import {
  getProviderBlockerMessage,
  validateRosterProviderEmail,
} from '../../components/AddProvidersToRosterPage/validation';
import {
  fetchFirstProviderByNpi,
  searchProvidersByName,
  verifyProviderByLast4,
} from '../serviceCalls/provider';
import { isValidLast4Format, isValidNpi } from '../validation';
import { CSV_HEADERS } from './constants';

/**
 * CSV Validation Error Types
 * Centralized constants for error type identifiers
 */
export const CSV_ERROR_TYPES = {
  REQUIRED: 'required',
  PROVIDER_NOT_FOUND: 'provider_not_found',
  FORMAT: 'format',
  LAST4_MISMATCH: 'last4_mismatch',
  INELIGIBLE: 'ineligible',
  DUPLICATE: 'duplicate',
  EMAIL_INVALID: 'email_invalid',
};

/**
 * @typedef {import('./parseRosterCSV').CSVRow} CSVRow
 * @typedef {import('../../components/AddProvidersToRosterPage/types').RosterProvider} RosterProvider
 * @typedef {import('../../types/provider').Provider} Provider
 */

/**
 * @typedef {Object} CSVValidationError
 * @property {number} rowNumber - CSV row number (1-based)
 * @property {string} type - Error type
 * @property {string} message - User-facing error message
 * @property {string} [providerName] - Provider name if found
 */

/**
 * @typedef {Object} CSVValidationResult
 * @property {boolean} isValid - Whether the row is valid
 * @property {CSVValidationError[]} errors - Array of validation errors
 * @property {RosterProvider|null} provider - Validated provider (if valid)
 * @property {number} rowNumber - Original CSV row number
 */

/**
 * @typedef {Object} CSVUploadResult
 * @property {CSVValidationResult[]} results - Validation results for each row
 * @property {number} totalRows - Total rows processed
 * @property {number} validRows - Number of valid rows
 * @property {number} errorRows - Number of rows with errors
 */

/**
 * Helper: Create error validation result
 * @param {CSVValidationError[]} errors - Array of validation errors
 * @param {RosterProvider | Provider | {displayName: string} | null} provider - Provider object or null
 * @param {number} rowNumber - CSV row number
 * @returns {CSVValidationResult}
 */
const createErrorResult = (errors, provider, rowNumber) => ({
  isValid: false,
  errors,
  provider,
  rowNumber,
});

/**
 * Helper: NPI format validation
 * @param {string} trimmedNpi - Trimmed NPI value
 * @param {string} csvProviderName - Provider name from CSV
 * @param {number} rowNumber - CSV row number
 * @returns {{errors: CSVValidationError[], shouldReturnEarly: boolean}}
 */
const validateNpiFormat = (trimmedNpi, csvProviderName, rowNumber) => {
  const errors = [];

  // NPI format validation
  if (!isValidNpi(trimmedNpi)) {
    const errorMsg = csvProviderName
      ? `The NPI for '${csvProviderName}' or CSV row #${rowNumber} was incorrect. Edit your CSV or search for this provider individually.`
      : `The NPI for CSV row #${rowNumber} was incorrect. Edit your CSV or search for this provider individually.`;

    errors.push({
      rowNumber,
      type: CSV_ERROR_TYPES.PROVIDER_NOT_FOUND,
      message: errorMsg,
    });

    return { errors, shouldReturnEarly: true };
  }

  return { errors, shouldReturnEarly: false };
};

/**
 * @param {string} last4 - Last 1–4 characters of DEA or license from CSV
 * @returns {string} Trimmed value (no padding; user-entered suffix is sent as-is)
 */
const trimLast4 = (last4) => (last4 == null ? '' : String(last4).trim());

/**
 * Zero-pad 1–3 alphanumeric chars to length 4 (CSV retry). Unchanged if already 4+ or non-alphanumeric.
 * @param {string} value - Trimmed last4
 * @returns {string}
 */
const padLast4ForRetry = (value) => {
  const t = value == null ? '' : String(value).trim();
  if (t.length === 0 || t.length >= 4) return t;
  if (!/^[A-Za-z0-9]+$/.test(t)) return t;
  return t.padStart(4, '0');
};

/**
 * Helper: Last4 and Email format validation
 * @param {CSVRow} row - CSV row data
 * @param {Array<RosterProvider>} existingProviders - Providers already in roster
 * @param {{ skipProfessionalCheck?: boolean }} [options={}] - Skip professional-domain check when email is from membership (autopopulated)
 * @returns {CSVValidationError[]}
 */
const validateLast4AndEmailFormats = (row, existingProviders, options = {}) => {
  const { skipProfessionalCheck = false } = options;
  const errors = [];

  // Last4 format validation (last4 is trimmed before this function is called)
  if (!isValidLast4Format(row.last4)) {
    errors.push({
      rowNumber: row.rowNumber,
      type: CSV_ERROR_TYPES.FORMAT,
      message: VALIDATION_MESSAGES.LAST4_INVALID,
    });
  }

  // Email format validation (skip professional check when email came from membership/API)
  const emailValidation = validateRosterProviderEmail(
    row.email,
    existingProviders,
    { skipProfessionalCheck },
  );
  if (!emailValidation.isValid) {
    errors.push({
      rowNumber: row.rowNumber,
      type: CSV_ERROR_TYPES.FORMAT,
      message:
        emailValidation.error?.message ??
        'Please enter a professional email address to proceed.',
    });
  }

  return errors;
};

/**
 * Helper: Provider lookup (NPI search, then name search)
 * @param {string} trimmedNpi - Trimmed NPI value
 * @param {string} csvProviderName - Provider name from CSV
 * @param {number} rowNumber - CSV row number
 * @returns {Promise<{provider: Provider | null, errors: CSVValidationError[]}>}
 */
const lookupProvider = async (trimmedNpi, csvProviderName, rowNumber) => {
  const errors = [];
  let foundProvider = null;

  // Try NPI search first
  try {
    const { provider, errorMessage } =
      await fetchFirstProviderByNpi(trimmedNpi);
    if (provider) {
      foundProvider = provider;
    } else if (errorMessage) {
      const errorMsg = csvProviderName
        ? `The NPI for '${csvProviderName}' or CSV row #${rowNumber} was incorrect. Edit your CSV or search for this provider individually.`
        : `The NPI for CSV row #${rowNumber} was incorrect. Edit your CSV or search for this provider individually.`;

      errors.push({
        rowNumber,
        type: CSV_ERROR_TYPES.PROVIDER_NOT_FOUND,
        message: errorMsg,
      });
    }
  } catch (error) {
    console.error('Error fetching provider by NPI:', error);
    const errorMsg = csvProviderName
      ? `The NPI for '${csvProviderName}' or CSV row #${rowNumber} was incorrect. Edit your CSV or search for this provider individually.`
      : `The NPI for CSV row #${rowNumber} was incorrect. Edit your CSV or search for this provider individually.`;

    errors.push({
      rowNumber,
      type: CSV_ERROR_TYPES.PROVIDER_NOT_FOUND,
      message: errorMsg,
    });
  }

  // If NPI search failed, try name search
  if (!foundProvider && csvProviderName) {
    try {
      const searchResponse = await searchProvidersByName(csvProviderName);

      if (searchResponse.success && searchResponse.results.length > 0) {
        foundProvider = searchResponse.results[0];
      } else {
        if (errors.length === 0) {
          errors.push({
            rowNumber,
            type: CSV_ERROR_TYPES.PROVIDER_NOT_FOUND,
            message: `${VALIDATION_MESSAGES.NAME_NOT_FOUND}: "${csvProviderName}". Please verify the name and try searching individually.`,
          });
        }
      }
    } catch (error) {
      console.error('Error searching provider by name:', error);
      if (errors.length === 0) {
        errors.push({
          rowNumber,
          type: CSV_ERROR_TYPES.PROVIDER_NOT_FOUND,
          message: `${VALIDATION_MESSAGES.NAME_NOT_FOUND}: "${csvProviderName}". Please verify the name and try searching individually.`,
        });
      }
    }
  }

  return { provider: foundProvider, errors };
};

/**
 * DEA/Last4 verification: try CSV value as-is, then (if different) zero-padded to 4 chars for numeric/alphanumeric short values.
 * @param {Provider} provider - Found provider
 * @param {string} last4Trimmed - Trimmed last4 from CSV
 * @param {number} rowNumber - CSV row number
 * @returns {Promise<{errors: CSVValidationError[], verifiedLast4: string | null}>}
 */
const verifyDEAWithRetry = async (provider, last4Trimmed, rowNumber) => {
  const mismatchError = () => ({
    rowNumber,
    type: CSV_ERROR_TYPES.LAST4_MISMATCH,
    message: 'Unable to find DEA/State License in our system - contact us',
    providerName: provider.displayName,
  });

  const tryOnce = async (value) => {
    const verifyResponse = await verifyProviderByLast4(
      provider.providerCode,
      value,
    );
    return verifyResponse.success;
  };

  try {
    if (await tryOnce(last4Trimmed)) {
      return { errors: [], verifiedLast4: last4Trimmed };
    }

    const padded = padLast4ForRetry(last4Trimmed);
    if (padded !== last4Trimmed && (await tryOnce(padded))) {
      return { errors: [], verifiedLast4: padded };
    }

    return { errors: [mismatchError()], verifiedLast4: null };
  } catch (error) {
    console.error('Error verifying provider by last4:', error);
    return {
      errors: [mismatchError()],
      verifiedLast4: null,
    };
  }
};

/**
 * Helper: Eligibility check (duplicate and subscription checks)
 * @param {Provider} provider - Found provider
 * @param {Array<RosterProvider>} existingProviders - Providers already in roster
 * @param {Array<{providerCode?: string}>} existingRosterProviders - Providers from backend
 * @param {number} rowNumber - CSV row number
 * @returns {{errors: CSVValidationError[]}}
 */
const checkEligibility = (
  provider,
  existingProviders,
  existingRosterProviders,
  rowNumber,
) => {
  const errors = [];

  const allExistingProviders = [
    ...existingProviders.map((p) => ({ providerCode: p.providerCode })),
    ...existingRosterProviders,
  ];

  const blockerMessage = getProviderBlockerMessage(
    provider,
    allExistingProviders,
  );
  if (blockerMessage) {
    if (blockerMessage.includes('sponsored')) {
      errors.push({
        rowNumber,
        type: CSV_ERROR_TYPES.INELIGIBLE,
        message: blockerMessage,
        providerName: provider.displayName,
      });
    } else {
      errors.push({
        rowNumber,
        type: CSV_ERROR_TYPES.DUPLICATE,
        message: blockerMessage,
        providerName: provider.displayName,
      });
    }
  }

  return { errors };
};

/**
 * Helper: Email validation
 * @param {string} email - Email address
 * @param {Array<RosterProvider>} existingProviders - Providers already in roster
 * @param {number} rowNumber - CSV row number
 * @param {{ skipProfessionalCheck?: boolean }} [options={}] - Skip professional check when email is from membership (autopopulated)
 * @returns {{errors: CSVValidationError[]}}
 */
const validateEmail = (email, existingProviders, rowNumber, options = {}) => {
  const { skipProfessionalCheck = false } = options;
  const errors = [];

  const emailValidationResult = validateRosterProviderEmail(
    email,
    existingProviders,
    { skipProfessionalCheck },
  );
  if (!emailValidationResult.isValid) {
    errors.push({
      rowNumber,
      type: CSV_ERROR_TYPES.EMAIL_INVALID,
      message:
        emailValidationResult.error?.message ??
        'Please enter a professional email address to proceed.',
    });
  }

  return { errors };
};

/**
 * Validate a single CSV row sequentially
 * If any step fails, remaining steps are skipped
 *
 * @param {CSVRow} row - CSV row data
 * @param {Array<RosterProvider>} existingProviders - Providers already in roster
 * @param {Array<{providerCode?: string}>} existingRosterProviders - Providers from backend
 * @returns {Promise<CSVValidationResult>}
 */
export const validateCSVRow = async (
  row,
  existingProviders = [],
  existingRosterProviders = [],
) => {
  const errors = [];

  // STEP 0: NPI is mandatory - check first and skip all other validations if missing
  // Trim NPI to ensure no whitespace issues
  const trimmedNpi = row.npi ? String(row.npi).trim() : '';

  // Build provider name from CSV data for display (used in error messages throughout validation)
  const csvProviderName =
    row.firstName && row.lastName
      ? `${row.firstName} ${row.lastName}`.trim()
      : '';

  if (!trimmedNpi) {
    return createErrorResult(
      [
        {
          rowNumber: row.rowNumber,
          type: CSV_ERROR_TYPES.REQUIRED,
          message: 'NPI field is required',
        },
      ],
      null,
      row.rowNumber,
    );
  }

  // STEP 1: NPI Format Validation
  // If NPI format is invalid, return early with just name (no provider details)
  const npiValidation = validateNpiFormat(
    trimmedNpi,
    csvProviderName,
    row.rowNumber,
  );
  errors.push(...npiValidation.errors);

  if (npiValidation.shouldReturnEarly) {
    return createErrorResult(
      errors,
      csvProviderName
        ? {
            displayName: csvProviderName,
          }
        : null,
      row.rowNumber,
    );
  }

  // STEP 2: Find Provider
  // If NPI is valid, fetch provider first to get full provider details
  const { provider: foundProvider, errors: lookupErrors } =
    await lookupProvider(trimmedNpi, csvProviderName, row.rowNumber);
  errors.push(...lookupErrors);

  if (!foundProvider) {
    return createErrorResult(
      errors,
      csvProviderName
        ? {
            displayName: csvProviderName,
          }
        : null,
      row.rowNumber,
    );
  }

  // STEP 2.25: Eligibility / duplicate provider check
  // If the provider is already in the roster (or ineligible), return that message early.
  // This avoids showing unrelated errors (like email format) for providers that can't be added anyway.
  const { errors: earlyEligibilityErrors } = checkEligibility(
    foundProvider,
    existingProviders,
    existingRosterProviders,
    row.rowNumber,
  );
  if (earlyEligibilityErrors.length > 0) {
    errors.push(...earlyEligibilityErrors);
    return createErrorResult(errors, foundProvider, row.rowNumber);
  }

  const trimmedLast4 = trimLast4(row.last4);

  // Effective email: use CSV email when present, otherwise provider email from membership (API)
  const csvEmail = row.email ? String(row.email).trim() : '';
  const membershipEmail = (
    foundProvider?.email ||
    foundProvider?.Email ||
    ''
  ).trim();

  const effectiveEmail = csvEmail || membershipEmail || '';

  // Email is required: must be present in CSV or in membership (API). Same treatment as other required fields.
  if (!effectiveEmail.trim()) {
    errors.push({
      rowNumber: row.rowNumber,
      type: CSV_ERROR_TYPES.REQUIRED,
      message: `Missing required fields: ${CSV_HEADERS[4]}`,
    });
    return createErrorResult(errors, foundProvider, row.rowNumber);
  }

  // STEP 2.5: Last4 and Email Format Validation
  // Validate Last4 and Email formats after provider is found
  // Use effectiveEmail
  const rowWithTrimmedLast4 = {
    ...row,
    last4: trimmedLast4,
    email: effectiveEmail,
  };
  const formatErrors = validateLast4AndEmailFormats(
    rowWithTrimmedLast4,
    existingProviders,
    { skipProfessionalCheck: !csvEmail.trim() },
  );
  errors.push(...formatErrors);

  // If format errors exist (Last4 or Email), return with full provider details
  if (formatErrors.length > 0) {
    return createErrorResult(errors, foundProvider, row.rowNumber);
  }

  // STEP 3: DEA Verification (raw value first, then zero-padded retry if needed)
  const { errors: deaErrors, verifiedLast4 } = await verifyDEAWithRetry(
    foundProvider,
    trimmedLast4,
    row.rowNumber,
  );
  errors.push(...deaErrors);

  if (errors.length > 0) {
    return createErrorResult(errors, foundProvider, row.rowNumber);
  }

  // STEP 4: Provider's Email Validation
  // Skip professional-domain check when email came from membership (autopopulated)
  const { errors: emailErrors } = validateEmail(
    effectiveEmail,
    existingProviders,
    row.rowNumber,
    { skipProfessionalCheck: !csvEmail.trim() },
  );
  errors.push(...emailErrors);

  // Build RosterProvider if all validations pass
  if (errors.length === 0) {
    const rosterProvider = {
      ...foundProvider,
      email: effectiveEmail,
      last4: verifiedLast4 ?? trimmedLast4,
      validEmail: true,
      validLast4: true,
    };
    if (!csvEmail.trim()) {
      rosterProvider.emailFromApi = effectiveEmail;
    }

    return {
      isValid: true,
      errors: [],
      provider: rosterProvider,
      rowNumber: row.rowNumber,
    };
  }

  // If email validation failed, still include provider so UI can display provider details with error
  return createErrorResult(errors, foundProvider, row.rowNumber);
};

/**
 * Validate all CSV rows sequentially
 * @param {CSVRow[]} rows - Array of CSV rows
 * @param {Array<RosterProvider>} existingProviders - Providers already in roster
 * @param {Array<{providerCode?: string}>} existingRosterProviders - Providers from backend
 * @returns {Promise<CSVUploadResult>}
 */
export const validateRosterCSV = async (
  rows,
  existingProviders = [],
  existingRosterProviders = [],
) => {
  const results = [];
  let currentExistingProviders = [...existingProviders];

  // Process rows sequentially to avoid API rate limiting
  for (const row of rows) {
    const result = await validateCSVRow(
      row,
      currentExistingProviders,
      existingRosterProviders,
    );
    results.push(result);

    // Add successfully validated providers to existingProviders for duplicate checking
    if (result.isValid && result.provider) {
      currentExistingProviders = [...currentExistingProviders, result.provider];
    }
  }

  const validRows = results.filter((r) => r.isValid).length;
  const errorRows = results.filter((r) => !r.isValid).length;

  return {
    results,
    totalRows: rows.length,
    validRows,
    errorRows,
  };
};
